import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { User, Briefcase, Lock, X, AlertCircle, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

const SALES_AGENT_PASSWORD = 'RenewBee-Bess-2026';

export const RoleSelectionSection = ({ formData, setFormData, t }) => {
  const role = t.roleSelection;
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(formData.userRole === 'salesAgent');
  const passwordInputRef = useRef(null);

  useEffect(() => {
    if (showPasswordModal && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [showPasswordModal]);

  const handleRoleChange = (value) => {
    if (value === 'salesAgent') {
      if (!isAuthenticated) {
        setShowPasswordModal(true);
        setPassword('');
        setPasswordError(false);
        return;
      }
    }
    setFormData(prev => ({
      ...prev,
      userRole: value
    }));
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === SALES_AGENT_PASSWORD) {
      setIsAuthenticated(true);
      setShowPasswordModal(false);
      setPasswordError(false);
      setPassword('');
      setFormData(prev => ({
        ...prev,
        userRole: 'salesAgent'
      }));
    } else {
      setPasswordError(true);
    }
  };

  const handlePasswordCancel = () => {
    setShowPasswordModal(false);
    setPassword('');
    setPasswordError(false);
  };

  const currentRole = formData.userRole || 'customer';

  return (
    <div className="space-y-6 animate-fade-in">
      <Card className="border-0 shadow-lg bg-gradient-card">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-xl text-foreground">
            <User className="w-5 h-5 text-primary" />
            {role.title}
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-2">{role.description}</p>
        </CardHeader>
        <CardContent className="space-y-6">
          <RadioGroup 
            value={currentRole}
            onValueChange={handleRoleChange}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            {/* Customer Option */}
            <Label 
              htmlFor="roleCustomer" 
              className={cn(
                "cursor-pointer rounded-xl border-2 p-6 transition-all duration-300 hover:border-primary/50",
                currentRole === 'customer' 
                  ? "border-primary bg-primary/5 shadow-glow" 
                  : "border-border bg-card"
              )}
            >
              <div className="flex items-start gap-4">
                <RadioGroupItem 
                  value="customer" 
                  id="roleCustomer" 
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-5 h-5 text-primary" />
                    <span className="font-semibold text-foreground">{role.customer}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {role.customerDescription}
                  </p>
                </div>
              </div>
            </Label>

            {/* Sales Agent Option - Password Protected */}
            <Label 
              htmlFor="roleSalesAgent" 
              className={cn(
                "cursor-pointer rounded-xl border-2 p-6 transition-all duration-300 hover:border-primary/50 relative",
                currentRole === 'salesAgent' 
                  ? "border-primary bg-primary/5 shadow-glow" 
                  : "border-border bg-card"
              )}
            >
              <div className="flex items-start gap-4">
                <RadioGroupItem 
                  value="salesAgent" 
                  id="roleSalesAgent" 
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Briefcase className="w-5 h-5 text-accent" />
                    <span className="font-semibold text-foreground">{role.salesAgent}</span>
                    {!isAuthenticated ? (
                      <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full ml-1">
                        <Lock className="w-3 h-3" />
                        {role.passwordProtected}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full ml-1">
                        <ShieldCheck className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {role.salesAgentDescription}
                  </p>
                </div>
              </div>
            </Label>
          </RadioGroup>

          {/* Info box based on selection */}
          <div className={cn(
            "p-4 rounded-lg border transition-all duration-300",
            currentRole === 'customer' 
              ? "bg-primary/5 border-primary/20" 
              : "bg-accent/5 border-accent/20"
          )}>
            <div className="flex items-center gap-3">
              {currentRole === 'customer' ? (
                <User className="w-6 h-6 text-primary" />
              ) : (
                <Briefcase className="w-6 h-6 text-accent" />
              )}
              <div>
                <p className="font-medium text-foreground">
                  {currentRole === 'customer' ? role.customerSelected : role.salesAgentSelected}
                </p>
                <p className="text-sm text-muted-foreground">
                  {currentRole === 'customer' 
                    ? role.customerFlow
                    : role.salesAgentFlow
                  }
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 px-6 py-5 border-b border-amber-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-amber-100 p-2 rounded-lg">
                    <Lock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">{role.passwordTitle}</h3>
                    <p className="text-sm text-gray-500">{role.passwordDescription}</p>
                  </div>
                </div>
                <button 
                  onClick={handlePasswordCancel}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <form onSubmit={handlePasswordSubmit} className="px-6 py-6 space-y-4">
              <div className="space-y-2">
                <Input
                  ref={passwordInputRef}
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError(false);
                  }}
                  placeholder={role.passwordPlaceholder}
                  className={cn(
                    "h-12 text-base",
                    passwordError && "border-red-400 focus:ring-red-200"
                  )}
                  autoComplete="off"
                />
                {passwordError && (
                  <div className="flex items-center gap-2 text-red-500 text-sm animate-fade-in">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{role.passwordError}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePasswordCancel}
                  className="flex-1"
                >
                  {role.passwordCancel}
                </Button>
                <Button
                  type="submit"
                  variant="premium"
                  className="flex-1"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {role.passwordSubmit}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleSelectionSection;
